import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings, Users, ShieldCheck, Palette, Database, ExternalLink, User as UserIcon,
  Sun, Moon, MonitorSmartphone, Globe, KeyRound,
} from 'lucide-react';
import ErpShell from '../components/ErpShell';
import { ErpPageHeader } from '../components/ErpPageHeader';
import { api, useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { LANGUAGES } from '../i18n';
import { useTranslation } from 'react-i18next';

const roleColor = (role) => role === 'ADMIN' ? 'accent' : role === 'ACCOUNTANT' ? 'info' : 'emerald';
const roleLabel = (role) => role === 'ADMIN' ? 'Owner' : role === 'ACCOUNTANT' ? 'Accountant' : 'Employee';

export default function ErpSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { i18n } = useTranslation();
  const isAdmin = user?.role === 'ADMIN';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [themePref, setThemePref] = useState(() => {
    try { return localStorage.getItem('gg_theme_pref') || 'system'; } catch { return 'system'; }
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/users');
      setUsers(r.data.users || []);
    } catch {
      // non-admins may not have permission — that's fine
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

  const stats = useMemo(() => ({
    owners: users.filter((u) => u.role === 'ADMIN').length,
    accountants: users.filter((u) => u.role === 'ACCOUNTANT').length,
    workers: users.filter((u) => u.role === 'WORKER').length,
  }), [users]);

  const updateRole = async (userId, role) => {
    try {
      await api.put(`/users/${userId}/role`, { role });
      toast({ title: 'Role updated' });
      load();
    } catch { toast({ title: 'Failed to update role', variant: 'destructive' }); }
  };

  const setTheme = (pref) => {
    setThemePref(pref);
    try { localStorage.setItem('gg_theme_pref', pref); } catch { /* ignore */ }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const resolved = pref === 'system' ? mq.matches : pref === 'dark';
    document.documentElement.classList.toggle('dark', resolved);
  };

  return (
    <ErpShell title="Settings">
      <ErpPageHeader
        eyebrow="Preferences"
        title="Settings"
        subtitle="Users, roles, theme, language and system tools."
        icon={Settings}
      />

      {/* Profile card */}
      <div className="gg-card p-6 mb-5" data-testid="profile-card">
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-3xl flex items-center justify-center shrink-0 text-lg font-bold"
            style={{
              background: 'linear-gradient(135deg, var(--gg-accent), #E2C15B)',
              color: '#fff',
              boxShadow: 'var(--gg-accent-glow)',
            }}
          >
            {(user?.name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <p className="gg-eyebrow">Signed in as</p>
            <p className="gg-hero-title" style={{ fontSize: 22 }}>{user?.name}</p>
            <p className="text-sm" style={{ color: 'var(--gg-text-secondary)' }}>
              {user?.email} · <span style={{ color: 'var(--gg-accent)', fontWeight: 700 }}>{roleLabel(user?.role)}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Preferences grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        {/* Theme */}
        <div className="gg-card p-6" data-testid="theme-card">
          <div className="flex items-center gap-2 mb-3">
            <Palette size={14} style={{ color: 'var(--gg-accent)' }} />
            <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>Theme</p>
          </div>
          <h3 className="gg-section-title" style={{ fontSize: 18 }}>Appearance</h3>
          <p className="text-xs mt-1 mb-4" style={{ color: 'var(--gg-text-dim)' }}>Choose how the app looks on this device.</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: 'light', label: 'Light', Icon: Sun },
              { key: 'dark',  label: 'Dark',  Icon: Moon },
              { key: 'system', label: 'System', Icon: MonitorSmartphone },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setTheme(key)}
                className={`p-4 rounded-2xl border transition-all text-center ${themePref === key ? 'font-semibold' : ''}`}
                style={{
                  background: themePref === key ? 'var(--gg-accent-soft)' : 'var(--gg-surface-2)',
                  color: themePref === key ? 'var(--gg-accent)' : 'var(--gg-text)',
                  borderColor: themePref === key ? 'var(--gg-accent)' : 'transparent',
                }}
                data-testid={`theme-opt-${key}`}
              >
                <Icon size={20} className="mx-auto mb-2" />
                <p className="text-sm">{label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Language */}
        <div className="gg-card p-6" data-testid="language-card">
          <div className="flex items-center gap-2 mb-3">
            <Globe size={14} style={{ color: 'var(--gg-info)' }} />
            <p className="gg-eyebrow" style={{ color: 'var(--gg-info)' }}>Language</p>
          </div>
          <h3 className="gg-section-title" style={{ fontSize: 18 }}>Interface language</h3>
          <p className="text-xs mt-1 mb-4" style={{ color: 'var(--gg-text-dim)' }}>Choose your preferred language (RTL is automatic).</p>
          <div className="grid grid-cols-3 gap-2">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => i18n.changeLanguage(l.code)}
                className={`p-4 rounded-2xl border transition-all text-center`}
                style={{
                  background: i18n.language === l.code ? 'var(--gg-accent-soft)' : 'var(--gg-surface-2)',
                  color: i18n.language === l.code ? 'var(--gg-accent)' : 'var(--gg-text)',
                  borderColor: i18n.language === l.code ? 'var(--gg-accent)' : 'transparent',
                  fontWeight: i18n.language === l.code ? 700 : 500,
                }}
                data-testid={`lang-opt-${l.code}`}
              >
                <span className="text-xl block mb-1">{l.flag}</span>
                <p className="text-xs">{l.name}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Users management */}
      {isAdmin ? (
        <div className="gg-card p-6 mb-5" data-testid="users-card">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} style={{ color: 'var(--gg-emerald)' }} />
                <p className="gg-eyebrow" style={{ color: 'var(--gg-emerald)' }}>Access</p>
              </div>
              <h3 className="gg-section-title mt-1" style={{ fontSize: 18 }}>Team ({users.length})</h3>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
            <MiniStat label="Owners" value={stats.owners} tone="accent" />
            <MiniStat label="Accountants" value={stats.accountants} tone="info" />
            <MiniStat label="Workers" value={stats.workers} tone="emerald" />
          </div>

          {loading ? (
            <div className="animate-pulse space-y-2">
              {[1,2,3].map((i) => <div key={i} className="h-12 rounded-xl" style={{ background: 'var(--gg-surface-2)' }} />)}
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: 'var(--gg-text-dim)' }}>No users found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="gg-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th style={{ width: 240 }}>Change role</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <motion.tr
                      key={u.id}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.25, delay: Math.min(idx * 0.02, 0.3) }}
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="font-semibold flex items-center gap-2">
                        <span className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold" style={{ background: 'var(--gg-surface-2)', color: 'var(--gg-text)' }}>
                          {(u.name || '?').charAt(0).toUpperCase()}
                        </span>
                        {u.name}
                      </td>
                      <td style={{ color: 'var(--gg-text-secondary)' }}>{u.email}</td>
                      <td><span className={`gg-chip gg-chip-${roleColor(u.role)}`}>{roleLabel(u.role)}</span></td>
                      <td>
                        <select
                          value={u.role}
                          onChange={(e) => updateRole(u.id, e.target.value)}
                          className="gg-input"
                          disabled={u.id === user?.id}
                          data-testid={`role-select-${u.id}`}
                        >
                          <option value="ADMIN">Owner (Admin)</option>
                          <option value="ACCOUNTANT">Accountant</option>
                          <option value="WORKER">Employee (Worker)</option>
                        </select>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="gg-card p-6 mb-5" data-testid="access-restricted">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: 'var(--gg-surface-2)', color: 'var(--gg-text-dim)' }}>
              <KeyRound size={18} />
            </span>
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--gg-text)' }}>User management is Owner-only</p>
              <p className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>Ask your owner to add or promote team members.</p>
            </div>
          </div>
        </div>
      )}
    </ErpShell>
  );
}

const MiniStat = ({ label, value, tone }) => (
  <div className="p-4 rounded-2xl" style={{ background: 'var(--gg-surface-2)' }}>
    <p className="gg-eyebrow">{label}</p>
    <p className="gg-tabular mt-1" style={{ fontSize: 24, fontWeight: 800, color: `var(--gg-${tone === 'accent' ? 'accent' : tone === 'info' ? 'info' : 'emerald'})` }}>
      {value}
    </p>
  </div>
);

const ToolCard = ({ icon: Icon, title, subtitle, href }) => (
  <a href={href} className="gg-card gg-card-hover p-5 flex items-center gap-4" data-testid={`tool-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: 'var(--gg-accent-soft)', color: 'var(--gg-accent)' }}>
      <Icon size={18} />
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold" style={{ color: 'var(--gg-text)' }}>{title}</p>
      <p className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>{subtitle}</p>
    </div>
    <ExternalLink size={14} style={{ color: 'var(--gg-text-dim)' }} />
  </a>
);
